import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGetSeries } from '@/v2/hooks/useSeries.js'
import Typography from '@/v2/components/common/Typography'
import { usePatientContext } from '@/v2/contexts/PatientContext'

const parse = value => parseInt(value, 10)

const SubFolders = ({ folder, autoNavigate }) => {
  const navigate = useNavigate()
  const { id, progression, series } = useParams()
  const [subFolders, setSubFolders] = useState([])
  const { patient, progressions, onAddProgressionSeries } = usePatientContext()
  const hasAddedSeriesRef = useRef(false)
  const hasAutoNavigatedRef = useRef(false)
  const prevFolderRef = useRef(folder)
  const progressionsRef = useRef(progressions)

  const { loading, error } = useGetSeries(patient.id, folder, data => {
    setSubFolders(data || [])
  })

  useEffect(() => {
    progressionsRef.current = progressions
  }, [progressions])

  useEffect(() => {
    if (prevFolderRef.current !== folder) {
      hasAddedSeriesRef.current = false
      hasAutoNavigatedRef.current = false
      prevFolderRef.current = folder
    }
  }, [folder])

  useEffect(() => {
    if (loading || error || subFolders.length === 0) {
      return
    }

    const currentProgression = progressionsRef.current.find(
      ({ id }) => parse(id) === parse(folder)
    )
    const hasSeries = !!currentProgression?.series

    if (!hasSeries && !hasAddedSeriesRef.current) {
      hasAddedSeriesRef.current = true
      onAddProgressionSeries(subFolders, folder)
    }

    // Auto-navigate to first series when folder changes (mobile)
    const filteredSeries = subFolders.filter(({ name }) => name !== 'Images')
    const isCurrentFolder = parse(progression) === parse(folder)
    const needsNavigation =
      autoNavigate &&
      !hasAutoNavigatedRef.current &&
      filteredSeries.length > 0 &&
      !isCurrentFolder

    if (needsNavigation) {
      hasAutoNavigatedRef.current = true
      navigate(
        `/v2/patient/${id}/imaging/progression/${folder}/series/${filteredSeries[0].id}`,
        { replace: true }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subFolders, loading, error, folder, autoNavigate])

  return subFolders
    .filter(({ name }) => name !== 'Images')
    .map(serie => (
      <Typography
        capitalize
        key={serie.id}
        bold={
          parse(serie.id) === parse(series) &&
          parse(progression) === parse(folder)
        }
        fontSize="12px"
        lineHeight="24px"
        cursor="pointer"
        onClick={() =>
          navigate(
            `/v2/patient/${id}/imaging/progression/${folder}/series/${serie.id}`
          )
        }
      >
        {serie.name}
      </Typography>
    ))
}

export default SubFolders
